import React from "react";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { MoreHoriz } from "@mui/icons-material";
import { tallyTokens } from "../../theme.tsx";

export interface RowMenuAction {
  /** Stable key; also used for the React list key. */
  id: string;
  icon: React.ReactNode;
  label: React.ReactNode;
  onClick: () => void;
  /** Renders in the critical colour and separates from the actions above. */
  destructive?: boolean;
}

interface RowMenuProps {
  actions: RowMenuAction[];
  /** Accessible name for the trigger, e.g. "Conversation options". */
  ariaLabel: string;
}

/**
 * The overflow menu on a drawer row.
 *
 * A real menu rather than a tooltip holding a list: it opens on click, traps
 * focus, closes on Escape or an outside click, and positions itself against
 * the trigger - none of which the previous tooltip did.
 */
const RowMenu: React.FC<RowMenuProps> = ({ actions, ariaLabel }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    // The row underneath is itself a button; without this, opening the menu
    // would also re-select the row.
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (
    event: React.MouseEvent<HTMLElement>,
    action: RowMenuAction,
  ) => {
    event.stopPropagation();
    setAnchorEl(null);
    action.onClick();
  };

  return (
    <React.Fragment>
      <IconButton
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open || undefined}
        onClick={handleOpen}
        sx={{
          flexShrink: 0,
          width: 26,
          height: 26,
          borderRadius: `${tallyTokens.radius.sm}px`,
          color: tallyTokens.ink3,
          "&:hover": {
            backgroundColor: tallyTokens.surface2,
            color: tallyTokens.ink,
          },
          "& svg": { fontSize: 18 },
        }}
      >
        <MoreHoriz />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: "4px",
              minWidth: 168,
              padding: "6px",
              borderRadius: `${tallyTokens.radius.md}px`,
              border: `1px solid ${tallyTokens.line}`,
              boxShadow: tallyTokens.shadow.lg,
              backgroundColor: tallyTokens.surface,
            },
          },
          list: { sx: { padding: 0 } },
        }}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.id}
            onClick={(event) => handleAction(event, action)}
            sx={{
              gap: "10px",
              minHeight: 0,
              padding: "8px 10px",
              borderRadius: `${tallyTokens.radius.sm}px`,
              fontSize: "0.85rem",
              lineHeight: 1.35,
              color: action.destructive ? tallyTokens.crit : tallyTokens.ink2,
              // Sets this apart from the actions above it.
              marginTop: action.destructive ? "2px" : 0,
              "&:hover": {
                backgroundColor: action.destructive
                  ? tallyTokens.critWash
                  : tallyTokens.surface2,
                color: action.destructive ? tallyTokens.crit : tallyTokens.ink,
              },
            }}
          >
            {action.icon}
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </React.Fragment>
  );
};

export default RowMenu;
